/* eslint-disable complexity */
define([
    'santa-components',
    'lodash',
    'prop-types',
    'coreUtils',
    'components/components/bootstrap/balata/balata'
], function (
    santaComponents,
    _,
    PropTypes,
    coreUtils,
    balata
) {
    'use strict';
    const consts = coreUtils.mediaConsts;
    const bgUtils = coreUtils.containerBackgroundUtils;

    /**
     * Look for media data in compDesign and fallback to compData
     * @param {object} props
     * @returns {{}}
     */
    function getMediaData(props) {
        return _.get(props.compDesign, ['background', 'mediaRef'], _.get(props.compData, ['background', 'mediaRef'])) || {};
    }

    /**
     * Look for image data in media data
     * @param {object} media
     * @returns {{}}
     */
    function getImageData(media) {
        return media.mediaType === 'WixVideo' ? media.posterImageRef : media;
    }

    /**
     * Look for the background in compDesign and fallback to compData
     * @param {object} props
     * @returns {{}}
     */
    function getBackgroundData(props) {
        return _.get(props.compDesign, ['background'], _.get(props.compData, ['background'])) || {};
    }


    /**
     * For MediaPlayer we pass video attibutes in the props where for older components they are in the data.
     * Hopefully this will be unified
     * @param {object} data
     * @param {object} properties
     * @returns {{loop: boolean, mute: boolean, autoplay: boolean}}
     */
    function getMediaAttributes(data, properties) {
        const dataAttr = _.pick(data, consts.playback.SUPPORTED_MEDIA_ATTRIBUTES);
        const propertiesAttr = _.pick(properties, consts.playback.SUPPORTED_MEDIA_ATTRIBUTES.concat(consts.supportedParentProps));

        return _.assign(dataAttr, propertiesAttr);
    }

    return {
        propTypes: _.defaults({
            id: santaComponents.santaTypesDefinitions.Component.id.isRequired,
            styleId: santaComponents.santaTypesDefinitions.Component.styleId.isRequired,
            compProp: santaComponents.santaTypesDefinitions.Component.compProp,
            compData: santaComponents.santaTypesDefinitions.Component.compData,
            compDesign: santaComponents.santaTypesDefinitions.Component.compDesign,
            compStaticBehaviors: santaComponents.santaTypesDefinitions.Component.compStaticBehaviors,
            isExperimentOpen: santaComponents.santaTypesDefinitions.isExperimentOpen,
            isInSeo: santaComponents.santaTypesDefinitions.isInSeo,
            bgStyle: PropTypes.object,
            shouldRenderSrc: santaComponents.santaTypesDefinitions.Media.shouldRenderSrc,
            onClick: PropTypes.func,
            // Video props
            mediaAspect: santaComponents.santaTypesDefinitions.SiteAspects.mediaAspect.isRequired,
            renderParts: santaComponents.santaTypesDefinitions.Media.renderParts,
            playbackFormat: santaComponents.santaTypesDefinitions.Media.playbackFormat,
            playbackConfig: santaComponents.santaTypesDefinitions.Media.playbackConfig,
            playbackUrl: santaComponents.santaTypesDefinitions.Media.playbackUrl,
            isMobileView: santaComponents.santaTypesDefinitions.isMobileView.isRequired,
            isPlayingAllowed: santaComponents.santaTypesDefinitions.RenderFlags.isPlayingAllowed.isRequired,
            enableBackgroundVideo: santaComponents.santaTypesDefinitions.Media.enableBackgroundVideo
        },
        santaComponents.utils.santaTypesUtils.getSantaTypesByDefinition(balata)
        ),

        getInitialState() {
            this.isInViewport = false;
            this.mediaAPI = null;
            this.currentVisibilityState = {hidden: false};
            return {};
        },

        componentDidMount() {
            this.props.mediaAspect.initFeatureDetections();
            this.registerPlayer(this.props);
        },

        componentWillUnmount() {
            this.unregisterPlayer(this.props);
        },

        componentWillReceiveProps(nextProps) {
            this.handleVideoDataChange(this.props, nextProps);
        },

        /**
         * automaticly play a video when it loads if needed
         * @param props
         */
        handleAutoplay(props) {
            const isAutoPlay = getMediaData(props).autoplay;
            if (this.isInViewport && isAutoPlay && props.isPlayingAllowed) {
                this._playMedia({persist: true});
            }
        },

        handleVideoDataChange(props, nextProps) {
            const nextMediaData = getMediaData(nextProps);
            const prevMediaData = getMediaData(props);

            // re-register the player in media aspect with the new video id
            if (nextMediaData.videoId && nextMediaData.videoId !== prevMediaData.videoId || // eslint-disable-line no-mixed-operators
                _.includes(nextProps.renderParts.media.video, 'video') && !_.includes(props.renderParts.media.video, 'video')) { // eslint-disable-line no-mixed-operators
                this.unregisterPlayer(props);
                this.registerPlayer(nextProps);
            } else if (nextMediaData.autoplay !== prevMediaData.autoplay && props.isPlayingAllowed) {
                // For hoverbox play/pause video on mode change
                if (nextMediaData.autoplay) {
                    this._playMedia();
                } else {
                    this.pauseMedia();
                }
            }
        },

        registerPlayer(props) {
            // Register video to aspect if there is a video
            const mediaData = getMediaData(props);

            if (_.get(mediaData, 'type') === 'WixVideo' && _.includes(props.renderParts.media.video, 'video')) {
                const registrationOptions = {
                    playerType: 'mediaContainer',
                    mediaData,
                    services: {
                        viewport: {
                            callback: this.onViewportChange,
                            eventTypes: ['in', 'out']
                        },
                        visibility: {
                            callback: this.onVisibilityChange
                        },
                        fileAvailability: {
                            callback: this.onFileAvailability,
                            videoParts: [{
                                name: 'video',
                                quality: mediaData.qualities[0].quality
                            }]
                        }
                    }
                };

                props.mediaAspect.registerPlayer(props.id, registrationOptions);
            }
        },

        unregisterPlayer(props) {
            props.mediaAspect.unregisterPlayer(props.id);
        },

        updatePlayerState(props, state) {
            props.mediaAspect.updatePlayerState(props.id, state);
        },

        updateQualityState(props, videoId, state) {
            props.mediaAspect.updateQualityState(videoId, state);
        },
        /**
         * Updates from media changes
         * @param stateEvent
         */
        onMediaChange(stateEvent) {
            const currentState = this.props.mediaAspect.getData(this.props.id) || {};
            const state = this.mediaStateChangeLogic(stateEvent, currentState);

            if (stateEvent.type === consts.eventTypes.LOAD && state.playbackState === consts.playbackTypes.READY) {
                this.handleAutoplay(this.props);
            }

            this.updatePlayerState(this.props, state);
        },

        /**
         * Handle player event messaging
         * @param {{}} playerStateEvent Event from player
         * @param {{}} currentState from state
         * @returns {{}}
         */
        mediaStateChangeLogic(playerStateEvent, currentState) {
            let state = {};

            const mediaAttributes = getMediaAttributes(getMediaData(this.props), this.props.compProp);
            const isSeekingEvents = _.includes([
                consts.playbackTypes.SEEKING_ENDED,
                consts.playbackTypes.SEEKING,
                consts.playbackTypes.SEEK_PLAYING], playerStateEvent.playbackState);
            const isSilentPaused = playerStateEvent.playbackState === consts.playbackTypes.PAUSED && mediaAttributes.loop && currentState.currentTime >= currentState.duration;

            switch (playerStateEvent.type) {
                // Initial load
                case consts.eventTypes.MOUNT:
                    state = {
                        playbackState: playerStateEvent.playbackState,
                        volume: 1,
                        fullscreen: false,
                        muted: true,
                        currentTime: 0,
                        progress: 0
                    };
                    break;

                // Video load events - just fill the store with values
                // The only place where duration is updated is here
                case consts.eventTypes.LOAD:
                    state = _.omit(playerStateEvent, ['type', 'originalEventType']);
                    break;

                // Playback state changes (play, pause, seek etc.)
                case consts.eventTypes.PLAYSTATE:
                    switch (playerStateEvent.playbackState) {
                        // Handle loop state when reaching end of video
                        case consts.playbackTypes.PLAY_ENDED:
                            // when loop, replay on every state but SEEK_PAUSED
                            if (mediaAttributes.loop) {
                                this._playMedia(); // persist: true - but we merge it with the state we return
                                state = {
                                    previousPlaybackState: consts.playbackTypes.PLAYING
                                };
                            } else {
                                this.pauseMedia(); // persist: true - but we merge it with the state we return
                                state = {
                                    playbackState: consts.playbackTypes.PLAY_ENDED,
                                    previousPlaybackState: consts.playbackTypes.PAUSED
                                };
                            }
                            break;

                        // On any other update, if not inside seek state, update playbackState
                        default:
                            // since we do not implement seek in background videos, we will get PAUSED/SEEKING/SEEKING_ENDED" only after
                            // loop replay or after stopping and resetting the current time to 0
                            // It could only happen if we are currently playing so we reset playbackState to the previous one

                            if (isSeekingEvents || isSilentPaused) {
                                state = {
                                    playbackState: currentState.playbackState
                                };
                            } else {
                                state = {
                                    playbackState: playerStateEvent.playbackState
                                };
                            }
                    }
                    break;
                case consts.eventTypes.ERROR:
                    if (playerStateEvent.error === consts.errorTypes.NO_HLS_VIDEO) {
                        this.updateQualityState(this.props, getMediaData(this.props).videoId, {
                            error: '',
                            fallback: true
                        });
                    } else if (playerStateEvent.error === consts.errorTypes.NO_VIDEO_FOUND) {
                        this.updateQualityState(this.props, getMediaData(this.props).videoId, {
                            error: playerStateEvent.error
                        });
                    }
                    break;
                case consts.eventTypes.TIME_UPDATE:
                    state = {
                        currentTime: playerStateEvent.currentTime
                    };
                    break;
                case consts.eventTypes.PROGRESS:
                    state = {
                        progress: playerStateEvent.progress
                    };
                    break;
                case consts.eventTypes.VOLUME:
                    state = {
                        volume: playerStateEvent.volume,
                        muted: playerStateEvent.muted
                    };
                    break;
                case consts.eventTypes.RATE:
                    // TODO - Tom B. 20/12/2016
                    break;
            }

            return state;
        },

        videoVisibilityChangeHandler() {
            const params = this.currentVisibilityState;
            const currentState = this.props.mediaAspect.getData(this.props.id);

            if (!params.hidden && params.in) {
                if (currentState.previousPlaybackState === consts.playbackTypes.PLAYING) {
                    this._playMedia();
                } else if (currentState.playbackState === consts.playbackTypes.IDLE ||
                    currentState.playbackState === consts.playbackTypes.READY) {
                    this.handleAutoplay(this.props);
                }
            } else {
                // hidden or out of viewport
                this.pauseMedia();
            }
        },

        /**
         * Updates from viewport service changes
         * @param {{
                in: boolean,
                inPartialAbove: boolean,
                inPartialBelow: boolean,
                inCover: boolean,
                inContained: boolean,
                out: boolean,
                outAbove: boolean,
                outBelow: boolean,
                percentVisible: number 0..1,
                percentOfViewport: number 0..1,
                isFixed: boolean
            }} params
         */
        onViewportChange(params) {
            const shouldUpdateVideoState = this.props.isPlayingAllowed && !params.isFixed;
            this.isInViewport = !!params.in;

            Object.assign(this.currentVisibilityState, params);

            if (shouldUpdateVideoState) {
                this.videoVisibilityChangeHandler();
            }
        },

        /**
         * Updates from Page Visibility API service changes
         * @param {{hidden: boolean}} params
         */
        onVisibilityChange(params) {
            const shouldUpdateVideoState = this.props.isPlayingAllowed;

            Object.assign(this.currentVisibilityState, params);

            if (shouldUpdateVideoState) {
                this.videoVisibilityChangeHandler();
            }
        },

        /**
         * Updates form file availability (quality) service changes
         * @param {{videoId: string, availabilityState: string, type: string, readyQualities: array}} params
         */
        onFileAvailability(params) {
            this.updateQualityState(this.props, params.videoId, {readyQualities: params.readyQualities});
            this.updatePlayerState(this.props, {
                mediaReadyState: params.availabilityState
            });
        },

        /**
         *
         * @param mediaAPI
         */
        setMediaAPI(mediaAPI) {
            this.mediaAPI = mediaAPI;
        },

        /**
         * Propagate API calls to media element through components hierarchy
         * @param {string} command
         */
        mediaCommandsLogic(command) {
            // TODO - Tom B. 19/12/2016
            // Add Logic (viewport, visibility)
            const args = _.tail(arguments);
            const params = _.isEmpty(args) ? undefined : args;
            if (this.mediaAPI) {
                this.mediaAPI(command, params);
            }
        },

        playPreview() {
            this.mediaCommandsLogic('play');
        },

        playMedia() {
            this._playMedia({persist: true});
        },

        pauseMedia() {
            this.mediaCommandsLogic('pause');
        },

        stopMedia() {
            this.mediaCommandsLogic('stop');
        },

        _playMedia({persist} = {}) {
            if (persist) {
                this.updatePlayerState(
                    this.props,
                    {previousPlaybackState: consts.playbackTypes.PLAYING}
                );
            }
            this.mediaCommandsLogic('play');
        },

        /**
         * Create a balata component
         * @param {{
         *       styleId: string,
         *       id: string,
         *       compData: object,
         *       bgStyle: object,
         *       onClick: function,
         *       compStaticBehaviors: object,
         *       compDesign: object
         *  }} [propsOverrides]
         *
         * @returns {ReactCompositeComponent}
         */
        createFillLayers(propsOverrides) {
            const props = _.assign({}, this.props, propsOverrides);
            const skinData = {
                skin: 'skins.viewer.balata.balataBaseSkin',
                styleId: props.styleId + consts.balataConsts.BALATA
            };
            const mediaData = getMediaData(props);
            const bgData = getBackgroundData(props);
            //#SE-21185 : adding different key when switching preview modes.
            //todo: revisit and understand why we lose ref to balata on mobile to desktop editor change (lightbox + hoverbox  scenario)
            const editorView = props.isMobileView ? 'mobile' : 'desktop';
            const extraProps = {
                ref: consts.balataConsts.BALATA,
                id: props.id + consts.balataConsts.BALATA,
                key: (props.isPlayingAllowed ? 'playback' : 'no-playback') + editorView,
                parentId: props.id,
                style: _.defaults(props.bgStyle, consts.defaultMediaStyle, {width: '100%', height: '100%'}),
                compBehaviors: props.compStaticBehaviors,
                compProp: getMediaAttributes(getMediaData(this.props), this.props.compProp),
                compDesign: props.compDesign,
                onClick: props.onClick,
                notifyMediaState: this.onMediaChange,
                setMediaAPI: this.setMediaAPI,
                isPlayingAllowed: props.isPlayingAllowed,
                shouldRenderSrc: props.shouldRenderSrc,
                // Disable video on mobile for background videos
                enableVideo: this.props.enableBackgroundVideo,
                enableBackgroundPadding: props.isMobileView,
                //layers rendering by existing data
                renderParts: props.renderParts,
                playbackFormat: props.playbackFormat,
                playbackConfig: props.playbackConfig,
                playbackUrl: props.playbackUrl
            };
            const imageData = getImageData(mediaData);
            if (this.props.shouldRenderSrc && !_.isEmpty(imageData)) {
                const mediaDimensions = bgUtils.convertStyleToDimensions(_.defaults(props.mediaDimensions, _.pick(props.style, ['width', 'height'])));
                extraProps.mediaDimensions = mediaDimensions;
                extraProps.imageUrlPreMeasureParams = bgUtils.getImageUrlPreMeasureParams(mediaDimensions, imageData, bgData.fittingType, bgData.alignType, props.isMobileView, props.isInSeo);
            }

            return this.createChildComponent(
                props.compData,
                'wysiwyg.viewer.components.background.Balata',
                skinData,
                extraProps
            );
        }
    };
});
